import { HelpModal } from '@components'

import { Button, Color, DrawerMode } from '@billy/design-system'
import React, { ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentFilterType } from '@features/attachments/enums/attachmentFiltertype'
import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { useFetchScoutConnections } from '@views/receipts'

import { EmberRoute } from '../../../../../enums/emberRoute'
import { Scope } from '../../../../../enums/scope'
import { useEmberRouteUrl } from '../../../../../hooks/routing/useEmberRouteUrl'
import { useOrganizationViewUrl } from '../../../../../hooks/routing/useOrganizationViewUrl'
import { useOpenHelpCenter } from '../../../../../hooks/useOpenHelpCenter'
import { useOrganizationSource } from '../../../../../hooks/useOrganizationSource'
import { isAuthorized } from '../../../../../utils/isAuthorized'
import { reactRoute } from '../../../../../utils/routing/reactRoute'
import { widgetId } from '../../../constants/widgetId'
import { useWidgetsActions } from '../../../hooks/useWidgetsActions'
import { CarouselUpsellWidgetId } from '../enums/carouselUpsellWidgetId'

export interface CarouselUpsellWidget {
  backgroundColor: Color
  description: string
  id: CarouselUpsellWidgetId
  img: { src: string; alt: string }
  shouldDisplay: boolean
  subtitle: string
  renderButton: () => ReactNode
  title: string
}

export const useCarouselUpsellWidgets = () => {
  const { isHelpCenterEnabled, openHelp } = useOpenHelpCenter()
  const { t } = useTranslation()
  const { connections: scoutConnections, isLoading: isScoutConnectionsLoading } = useFetchScoutConnections()
  const hasScoutConnections = !!scoutConnections?.length

  const { url: salaryIntegrationUrl } = useOrganizationViewUrl(reactRoute.organization.salary.getMainRoute())
  const { url: scoutUrl } = useOrganizationViewUrl(reactRoute.organization.uploads.getMainRoute(), {
    type: AttachmentFilterType.Scout,
    drawerMode: DrawerMode.Opened,
  })
  const { url: integrationsUrl } = useEmberRouteUrl(EmberRoute.Integrations)
  const { url: financingUrl } = useEmberRouteUrl(EmberRoute.Financing)
  const { url: settingsOrganizationUsersUrl } = useEmberRouteUrl(EmberRoute.SettingsOrganizationUsers)
  const { url: cardPaymentsSettingsUrl } = useEmberRouteUrl(EmberRoute.SettingsCardPayments)
  const { trackDashboardCarouselUpsellActionPerformed } = useWidgetsActions()

  const { isSourceZervant } = useOrganizationSource()
  const {
    hasPayNowSettings,
    isLoading: isPayNowStatusLoading,
    isError: isPayNowStatusError,
  } = usePayNowStatus({ enabled: isSourceZervant })
  const shouldDisplayStripePayment =
    isAuthorized(Scope.InvoiceRead) &&
    isSourceZervant &&
    !isPayNowStatusLoading &&
    !isPayNowStatusError &&
    hasPayNowSettings === false

  const trackUpsellWidgetAction = useCallback(
    (carouselUpsellWidgetId: CarouselUpsellWidgetId) => {
      trackDashboardCarouselUpsellActionPerformed({
        action: 1,
        widget: `${widgetId.CarouselUpsell} - ${carouselUpsellWidgetId}`,
      })
    },
    [trackDashboardCarouselUpsellActionPerformed],
  )

  const handleOnSupportButtonClick = useCallback(() => {
    trackUpsellWidgetAction(CarouselUpsellWidgetId.Support)
    openHelp()
  }, [openHelp, trackUpsellWidgetAction])

  const carouselUpsellActiveWidgets: CarouselUpsellWidget[] = [
    {
      id: CarouselUpsellWidgetId.Scout,
      shouldDisplay: !isScoutConnectionsLoading && !hasScoutConnections,
      subtitle: t('dashboard.widget.carousel-upsell.scout.subtitle'),
      title: t('dashboard.widget.carousel-upsell.scout.title'),
      description: t('dashboard.widget.carousel-upsell.scout.description'),
      backgroundColor: Color.Yellow100,
      img: { src: '/static-widgets/scout-discovery.svg', alt: 'Dashboard widget - scout' },
      renderButton: () => (
        <Button
          size="sm"
          to={scoutUrl}
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.Scout)}
        >
          {t('dashboard.widget.carousel-upsell.scout.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.StripePayment,
      shouldDisplay: shouldDisplayStripePayment,
      subtitle: t('dashboard.widget.carousel-upsell.stripe_payment.subtitle'),
      title: t('dashboard.widget.carousel-upsell.stripe_payment.title'),
      description: t('dashboard.widget.carousel-upsell.stripe_payment.description'),
      backgroundColor: Color.Yellow100,
      img: { src: '/static-widgets/stripe-payment.svg', alt: 'Dashboard widget - stripe payment' },
      renderButton: () => (
        <Button
          color="warning"
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.StripePayment)}
          size="sm"
          to={cardPaymentsSettingsUrl}
          variant="onColor"
        >
          {t('dashboard.widget.carousel-upsell.stripe_payment.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.Loans,
      shouldDisplay: isAuthorized(Scope.LoansWrite),
      subtitle: t('dashboard.widget.carousel-upsell.loans.subtitle'),
      title: t('dashboard.widget.carousel-upsell.loans.title'),
      description: t('dashboard.widget.carousel-upsell.loans.description'),
      backgroundColor: Color.Yellow100,
      img: { src: '/static-widgets/loans.svg', alt: 'Dashboard widget - loans' },
      renderButton: () => (
        <Button
          variant="onColor"
          color="warning"
          size="sm"
          to={financingUrl}
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.Loans)}
        >
          {t('dashboard.widget.carousel-upsell.loans.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.Integrations,
      shouldDisplay: isAuthorized(Scope.AddOns),
      subtitle: t('dashboard.widget.carousel-upsell.integrations.subtitle'),
      title: t('dashboard.widget.carousel-upsell.integrations.title'),
      description: t('dashboard.widget.carousel-upsell.integrations.description'),
      backgroundColor: Color.Grey100,
      img: { src: '/static-widgets/integrations.svg', alt: 'Dashboard widget - integrations' },
      renderButton: () => (
        <Button
          variant="onColor"
          color="primary"
          size="sm"
          to={integrationsUrl}
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.Integrations)}
        >
          {t('dashboard.widget.carousel-upsell.integrations.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.AddUsers,
      shouldDisplay: isAuthorized(Scope.UserWrite),
      subtitle: t('dashboard.widget.carousel-upsell.add_users.subtitle'),
      title: t('dashboard.widget.carousel-upsell.add_users.title'),
      description: t('dashboard.widget.carousel-upsell.add_users.description'),
      backgroundColor: Color.Yellow100,
      img: { src: '/static-widgets/add-users.svg', alt: 'Dashboard widget - Add users' },
      renderButton: () => (
        <Button
          variant="onColor"
          color="warning"
          size="sm"
          to={settingsOrganizationUsersUrl}
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.AddUsers)}
        >
          {t('dashboard.widget.carousel-upsell.add_users.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.Payroll,
      shouldDisplay: isAuthorized(Scope.SalaryWrite),
      subtitle: t('dashboard.widget.carousel-upsell.payroll.subtitle'),
      title: t('dashboard.widget.carousel-upsell.payroll.title'),
      description: t('dashboard.widget.carousel-upsell.payroll.description'),
      backgroundColor: Color.Orange50,
      img: { src: '/static-widgets/payroll.svg', alt: 'Dashboard widget - payroll' },
      renderButton: () => (
        <Button
          variant="filled"
          size="sm"
          to={salaryIntegrationUrl}
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.Payroll)}
        >
          {t('dashboard.widget.carousel-upsell.payroll.button.label')}
        </Button>
      ),
    },
    {
      id: CarouselUpsellWidgetId.Support,
      shouldDisplay: true,
      subtitle: t('dashboard.widget.carousel-upsell.support.subtitle'),
      title: t('dashboard.widget.carousel-upsell.support.title'),
      description: t('dashboard.widget.carousel-upsell.support.description'),
      backgroundColor: Color.Green100,
      img: { src: '/static-widgets/support.svg', alt: 'Dashboard widget - support' },
      renderButton: () => (
        <>
          <Button variant="onColor" color="success" size="sm" onClick={handleOnSupportButtonClick} fullWidth={false}>
            {t('dashboard.widget.carousel-upsell.support.button.label')}
          </Button>
          {!isHelpCenterEnabled && <HelpModal />}
        </>
      ),
    },
  ]

  const carouselUpsellDeprecatedWidgets: CarouselUpsellWidget[] = [
    {
      id: CarouselUpsellWidgetId.Feedback,
      shouldDisplay: true,
      subtitle: t('dashboard.widget.carousel-upsell.feedback.subtitle'),
      title: t('dashboard.widget.carousel-upsell.feedback.title'),
      description: t('dashboard.widget.carousel-upsell.feedback.description'),
      backgroundColor: Color.White,
      img: { src: '/static-widgets/feedback.svg', alt: 'Dashboard widget - feedback' },
      renderButton: () => (
        <Button
          variant="outlined"
          size="sm"
          href="https://form.typeform.com/to/ckHJuaAn"
          target="_blank"
          fullWidth={false}
          onClick={() => trackUpsellWidgetAction(CarouselUpsellWidgetId.Feedback)}
        >
          {t('dashboard.widget.carousel-upsell.feedback.button.label')}
        </Button>
      ),
    },
  ]

  const visibleCarouselUpsellWidgets: CarouselUpsellWidget[] = carouselUpsellActiveWidgets.filter(
    ({ shouldDisplay }) => shouldDisplay,
  )

  return { carouselUpsellWidgets: visibleCarouselUpsellWidgets, carouselUpsellDeprecatedWidgets }
}
